import React from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  Button,
  Table,
  FormGroup,
  Collapse,
  Badge,
} from "reactstrap";
import PropTypes from "prop-types";
import { createModuleLogger } from "../../utils/logger";

// Crear logger para este módulo
const corteLogger = createModuleLogger('CorteConversion');

// Default empty corte structure
const getDefaultCorte = (medidas, planeacionTamano) => {
  corteLogger.debug("Creando estructura de corte por defecto", { 
    medidasCount: medidas?.length || 0, 
    planeacionTamano 
  });
  
  // Si hay una medida en planeación, buscar su nombre para tamano_corte y su ID para impresion.tamano
  let tamanoCorteValue = "";
  let tamanoId = "";
  if (planeacionTamano && medidas && medidas.length > 0) {
    const medidaEncontrada = medidas.find(m => m.id === planeacionTamano);
    if (medidaEncontrada) {
      tamanoCorteValue = medidaEncontrada.nombre;
      tamanoId = medidaEncontrada.id;
    }
  }
  
  // Crear un nuevo corte con valores por defecto
  return {
    // ID will be assigned by PocketBase
    papel: "",
    parametros: [{ 
      // ID will be assigned by PocketBase 
      cantidad: "", 
      sobrante: "", 
      total: "",
      tamano_corte: tamanoCorteValue,
      tamano_cortado: "",
      detalles: "",
      // Agregamos el objeto de impresión directamente asociado a este parámetro
      impresion: {
        // ID will be assigned by PocketBase
        impresos: "", 
        sobrantes: "", 
        total: "",
        tamano: tamanoId, // Usamos el ID de la medida desde planeación
        pasadas: "",
        tinta_color: ""
      }
    }],
    isOpen: true
  };
};

/**
 * Componente para el formulario de Corte y Conversión en una orden de producción
 */
const CorteConversion = React.memo(({ data, onChange, materiales, medidas, planeacionTamano, onValidationError }) => {
  
  const cortes = React.useMemo(() => {
    const hasValidData = Array.isArray(data) && data.length > 0;
    
    if (!hasValidData) {
      corteLogger.debug("Inicializando corte por defecto", { 
        dataLength: data?.length || 0, 
        tienemedidas: !!medidas?.length,
        planeacionTamano 
      });
    }
    
    return hasValidData ? data : [getDefaultCorte(medidas, planeacionTamano)];
  }, [data, medidas, planeacionTamano]);

  // All handlers create new objects and call onChange directly
  const handlePapelChange = React.useCallback((corteIndex, value) => {
    const newCortes = JSON.parse(JSON.stringify(cortes));
    newCortes[corteIndex].papel = value;
    onChange({
      target: {
        name: "corte_conversion",
        value: newCortes
      }
    });
  }, [cortes, onChange]);

  const handleParametroChange = React.useCallback((corteIndex, paramIndex, field, value) => {
    const newCortes = JSON.parse(JSON.stringify(cortes));
    newCortes[corteIndex].parametros[paramIndex][field] = value;
    
    // Si se cambió cantidad o sobrante, calculamos el total automáticamente
    if (field === 'cantidad' || field === 'sobrante') {
      const parametro = newCortes[corteIndex].parametros[paramIndex];
      const cantidad = field === 'cantidad' ? value : parametro.cantidad;
      const sobrante = field === 'sobrante' ? value : parametro.sobrante;
      
      const cantidadNum = parseInt(cantidad) || 0;
      const sobranteNum = parseInt(sobrante) || 0;
      
      newCortes[corteIndex].parametros[paramIndex].total = (cantidadNum + sobranteNum).toString();
    }
    
    onChange({
      target: {
        name: "corte_conversion",
        value: newCortes
      }
    });
  }, [cortes, onChange]);

  const handleAddParametro = React.useCallback((corteIndex) => {
    const newCortes = JSON.parse(JSON.stringify(cortes));
    
    // Validar que se haya seleccionado un tipo de papel
    if (!newCortes[corteIndex].papel) {
      alert("Por favor seleccione un tipo de papel antes de agregar una fila");
      return;
    }
    
    // Si hay un tamaño de planeación seleccionado, usarlo
    // Si no, buscar un valor en otro parámetro existente de este corte
    let tamanoCorteValue = "";
    let tamanoId = "";
    
    // Primero, verificar si hay una medida en planeación
    if (planeacionTamano) {
      // Buscar el nombre de la medida correspondiente al ID
      const medidaEncontrada = medidas.find(m => m.id === planeacionTamano);
      if (medidaEncontrada) {
        tamanoCorteValue = medidaEncontrada.nombre;
        tamanoId = medidaEncontrada.id;
      }
    } 
    // Si no hay una medida en planeación, intentar usar el valor de otro parámetro
    else if (newCortes[corteIndex].parametros.length > 0 && newCortes[corteIndex].parametros[0].tamano_corte) {
      tamanoCorteValue = newCortes[corteIndex].parametros[0].tamano_corte;
      
      // Intentar encontrar el ID correspondiente al nombre
      const medidaEncontrada = medidas.find(m => m.nombre === tamanoCorteValue);
      if (medidaEncontrada) {
        tamanoId = medidaEncontrada.id;
      }
    }
    
    newCortes[corteIndex].parametros.push({ 
      // ID will be assigned by PocketBase
      cantidad: "", 
      sobrante: "", 
      total: "",
      tamano_corte: tamanoCorteValue,
      tamano_cortado: "",
      detalles: "",
      // Agregamos el objeto de impresión directamente asociado a este parámetro
      impresion: {
        // ID will be assigned by PocketBase
        impresos: "", 
        sobrantes: "", 
        total: "",
        tamano: tamanoId, // Usamos el ID de la medida 
        pasadas: "",
        tinta_color: ""
      }
    });
    
    onChange({
      target: {
        name: "corte_conversion",
        value: newCortes
      }
    });
  }, [cortes, onChange, planeacionTamano, medidas]);

  const handleRemoveParametro = React.useCallback((corteIndex, paramIndex) => {
    const newCortes = JSON.parse(JSON.stringify(cortes));
    const parametrosDelCorte = newCortes[corteIndex].parametros;
    
    // Se permite eliminar todas las filas
    newCortes[corteIndex].parametros = parametrosDelCorte.filter((_, i) => i !== paramIndex);
    
    // Disparamos el evento onChange para actualizar tanto corte_conversion como impresion
    onChange({
      target: {
        name: "corte_conversion",
        value: newCortes,
        // Añadimos los índices para que el componente padre sepa exactamente qué eliminar
        removedCorteIndex: corteIndex,
        removedParamIndex: paramIndex
      }
    });
  }, [cortes, onChange]);
  
  const handleAddCorte = React.useCallback(() => {
    const newCortes = [...cortes, getDefaultCorte(medidas, planeacionTamano)];
    
    onChange({
      target: {
        name: "corte_conversion",
        value: newCortes
      }
    });
    
    // Ya no necesitamos sincronizar con impresion, ya que cada parametro tiene su propia impresion
  }, [cortes, onChange, medidas, planeacionTamano]);
  
  const handleRemoveCorte = React.useCallback((index) => {
    // Se permite eliminar todos los cortes, pero siempre debe quedar al menos uno
    let newCortes;
    if (cortes.length <= 1) {
      // Si solo queda un corte, lo reseteamos en lugar de eliminarlo
      newCortes = [getDefaultCorte(medidas, planeacionTamano)];
      console.log("Reseteando el único corte que quedaba");
    } else {
      // Si hay más de un corte, eliminamos el solicitado
      newCortes = cortes.filter((_, i) => i !== index);
      console.log("Eliminando corte completo con índice:", index);
    }
    
    // Notificamos al componente padre sobre el cambio
    onChange({
      target: {
        name: "corte_conversion",
        value: newCortes,
        // Añadimos esta señal para indicar al componente padre que se ha eliminado un corte completo
        // y que debe sincronizar la tabla de impresión eliminando el índice correspondiente
        removedCorteIndex: index
      }
    });
    
    // El componente padre (orden_produccion.js) debe encargarse de sincronizar esta eliminación con impresion
  }, [cortes, onChange, medidas, planeacionTamano]);
  
  const toggleCorte = React.useCallback((index) => {
    const newCortes = JSON.parse(JSON.stringify(cortes));
    newCortes[index].isOpen = !newCortes[index].isOpen;
    
    onChange({
      target: {
        name: "corte_conversion",
        value: newCortes
      }
    });
  }, [cortes, onChange]);

  // Handler para los cambios en los datos de impresión
  const handleImpresionChange = React.useCallback((corteIndex, paramIndex, field, value) => {
    const newCortes = JSON.parse(JSON.stringify(cortes));
    
    // Asegurarse de que existe el objeto impresion
    if (!newCortes[corteIndex].parametros[paramIndex].impresion) {
      newCortes[corteIndex].parametros[paramIndex].impresion = {
        impresos: "",
        sobrantes: "",
        total: "",
        tamano: "",
        pasadas: "",
        tinta_color: ""
      };
    }
    
    newCortes[corteIndex].parametros[paramIndex].impresion[field] = value;
    
    // Si se cambió impresos o sobrantes, calculamos el total automáticamente
    if (field === 'impresos' || field === 'sobrantes') {
      const impresion = newCortes[corteIndex].parametros[paramIndex].impresion;
      const impresos = field === 'impresos' ? value : impresion.impresos;
      const sobrantes = field === 'sobrantes' ? value : impresion.sobrantes;
      
      const impresosNum = parseInt(impresos) || 0;
      const sobrantesNum = parseInt(sobrantes) || 0;
      
      impresion.total = (impresosNum + sobrantesNum).toString();
    }
    
    onChange({
      target: {
        name: "corte_conversion",
        value: newCortes
      }
    });
  }, [cortes, onChange]);

  const getMaterialName = React.useCallback((materialId) => {
    const material = materiales.find(m => m.id === materialId);
    return material ? `${material.nombre}${material.texto_medida ? ` - ${material.texto_medida}` : ''}` : "";
  }, [materiales]);

  // Validate data
  // Función para asegurarse de que cada parámetro tenga un objeto impresion inicializado
  const ensureImpresionExists = React.useCallback(() => {
    const needsUpdate = cortes.some(corte => 
      corte.parametros.some(param => !param.impresion)
    );
    
    if (needsUpdate) {
      const newCortes = JSON.parse(JSON.stringify(cortes));
      
      newCortes.forEach(corte => {
        corte.parametros.forEach(param => {
          if (!param.impresion) {
            // Si no existe el objeto impresion, lo creamos
            let tamanoId = "";
            if (param.tamano_corte && medidas) {
              // Buscar el ID correspondiente al nombre del tamano_corte
              const medidaEncontrada = medidas.find(m => m.nombre === param.tamano_corte);
              if (medidaEncontrada) {
                tamanoId = medidaEncontrada.id;
              }
            }
            
            param.impresion = {
              impresos: "", 
              sobrantes: "", 
              total: "",
              tamano: tamanoId,
              pasadas: "",
              tinta_color: ""
            };
          }
        });
      });
      
      onChange({
        target: {
          name: "corte_conversion",
          value: newCortes
        }
      });
    }
  }, [cortes, onChange, medidas]);

  // Al cargar el componente o al cambiar los datos, nos aseguramos de que exista el objeto impresion
  React.useEffect(() => {
    ensureImpresionExists();
  }, [ensureImpresionExists]);

  React.useEffect(() => {
    if (typeof onValidationError !== 'function') return;

    const hasPartialData = cortes.some(corte => 
      corte.papel || corte.parametros.some(param => 
        param.cantidad || param.sobrante || param.tamano_corte || param.tamano_cortado || param.detalles
      )
    );

    if (hasPartialData) {
      const hasMissingPaper = cortes.some(corte => !corte.papel);
      if (hasMissingPaper) {
        onValidationError({
          component: 'corte_conversion',
          message: 'Debe seleccionar un tipo de papel para todos los cortes'
        });
      } else {
        onValidationError({
          component: 'corte_conversion',
          message: null
        });
      }
    } else {
      onValidationError({
        component: 'corte_conversion',
        message: null
      });
    }
  }, [cortes, onValidationError]);

  return (
    <Card className="mb-3 border border-4 border-light">
      <CardBody>
        <h4 className="card-title mb-3">Corte y Conversión</h4>
        
        {/* Múltiples cortes (tipos de papel) */}
        {cortes.map((corte, corteIndex) => (
          <div key={corte.id || `corte-${corteIndex}`} className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2 bg-light p-2">
              <div className="d-flex align-items-center">
                <Button
                  color="link"
                  size="sm"
                  onClick={() => toggleCorte(corteIndex)}
                  className="p-0 me-2"
                >
                  <i className={`fas fa-chevron-${corte.isOpen ? 'down' : 'right'}`}></i>
                </Button>
                <h5 className="mb-0">
                  Corte {corteIndex + 1}
                  {corte.papel && (
                    <Badge color="info" className="ms-2">
                      {getMaterialName(corte.papel)}
                    </Badge>
                  )}
                </h5>
              </div>
              <div>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleRemoveCorte(corteIndex)}
                  className="me-2"
                >
                  <i className="fas fa-trash-alt me-1"></i> Eliminar
                </Button>
              </div>
            </div>
            
            <Collapse isOpen={corte.isOpen}>
              {/* Tipo de papel */}
              <Row className="mb-3">
                <Col md="12">
                  <FormGroup>
                    <Label className="form-label">Tipo de Papel</Label>
                    <Input
                      type="select"
                      className="form-select"
                      value={corte.papel || ""}
                      onChange={(e) => handlePapelChange(corteIndex, e.target.value)}
                    >
                      <option value="">Seleccionar tipo de papel...</option>
                      {materiales.map((material) => (
                        <option key={`corte-papel-${corteIndex}-${material.id}`} value={material.id}>
                          {material.nombre} {material.texto_medida ? `- ${material.texto_medida}` : ''}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              
              {/* Tabla de corte */}
              <Row className="mb-3">
                <Col md="12">
                  <div className="table-responsive">
                    <Table className="table-bordered">
                      <thead>
                        <tr>
                          <th style={{ width: "5%" }} rowSpan="2">N.º</th>
                          <th colSpan="3" style={{ width: "30%" }}>Pliego a Solicitar</th>
                          <th style={{ width: "15%" }}></th>
                          <th style={{ width: "15%" }}></th>
                          <th style={{ width: "5%" }} rowSpan="2">Acción</th>
                        </tr>
                        <tr>
                          <th style={{ width: "10%" }}></th>
                          <th style={{ width: "10%" }}></th>
                          <th style={{ width: "10%" }}></th>
                          <th colSpan="2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {corte.parametros.map((parametro, paramIndex) => (
                          <>
                            <tr key={`${parametro.id || `parametro-${corteIndex}-${paramIndex}`}-row1`}>
                              <td rowSpan="2">{paramIndex + 1}</td>
                              <td>
                                <Label className="form-label fw-bold">Cant.</Label>
                                <Input
                                  type="number"
                                  value={parametro.cantidad || ""}
                                  onChange={(e) => handleParametroChange(corteIndex, paramIndex, 'cantidad', e.target.value)}
                                  className="form-control form-control-sm"
                                />
                              </td>
                              <td>
                                <Label className="form-label fw-bold">Sobr.</Label>
                                <Input
                                  type="number"
                                  value={parametro.sobrante || ""}
                                  onChange={(e) => handleParametroChange(corteIndex, paramIndex, 'sobrante', e.target.value)}
                                  className="form-control form-control-sm"
                                />
                              </td>
                              <td>
                                <Label className="form-label fw-bold">Total</Label>
                                <Input
                                  type="number"
                                  value={parametro.total || ""}
                                  readOnly
                                  className="form-control form-control-sm bg-light"
                                />
                              </td>
                              <td>
                                <Label className="form-label fw-bold">Tamaño Corte</Label>
                                <Input
                                  type="text"
                                  value={parametro.tamano_corte || ""}
                                  readOnly
                                  className="form-control form-control-sm bg-light"
                                  placeholder="Automático desde Planeación"
                                />
                              </td>
                              <td>
                                <Label className="form-label fw-bold">Tamaños Cortados</Label>
                                <Input
                                  type="text"
                                  value={parametro.tamano_cortado || ""}
                                  onChange={(e) => handleParametroChange(corteIndex, paramIndex, 'tamano_cortado', e.target.value)}
                                  className="form-control form-control-sm"
                                />
                              </td>
                              <td className="text-center" rowSpan="2">
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleRemoveParametro(corteIndex, paramIndex)}
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </Button>
                              </td>
                            </tr>
                            <tr key={`${parametro.id || `parametro-${corteIndex}-${paramIndex}`}-row2`}>
                              <td colSpan="5">
                                <Label className="form-label fw-bold">Detalles</Label>
                                <Input
                                  type="textarea"
                                  value={parametro.detalles || ""}
                                  onChange={(e) => handleParametroChange(corteIndex, paramIndex, 'detalles', e.target.value)}
                                  className="form-control form-control-sm"
                                  placeholder="Ingrese detalles adicionales"
                                  rows="3"
                                  style={{ minHeight: "75px", resize: "vertical" }}
                                />
                              </td>
                            </tr>
                          </>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => handleAddParametro(corteIndex)}
                    className="mt-2"
                  >
                    <i className="fas fa-plus me-1"></i> Agregar Fila
                  </Button>
                </Col>
              </Row>
            </Collapse>
          </div>
        ))}
        
        <div className="mt-3">
          <Button
            color="success"
            onClick={handleAddCorte}
          >
            <i className="fas fa-plus-circle me-1"></i> Agregar Nuevo Tipo de Papel
          </Button>
        </div>
      </CardBody>
    </Card>
  );
});

CorteConversion.propTypes = {
  data: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  materiales: PropTypes.array,
  medidas: PropTypes.array,
  planeacionTamano: PropTypes.string,
  onValidationError: PropTypes.func
};

CorteConversion.defaultProps = {
  data: [],
  materiales: [],
  medidas: [],
  planeacionTamano: ""
};

export default CorteConversion;
